// teamService.js - نسخه نهایی نهایی (مقیاس‌پذیر + بدون Race Condition + defensive کامل)
// ============================================================
// تغییرات کلیدی نسبت به نسخه قبل:
//   ۱. حذف saveReactionToServer معیوب (فرمت متفاوت، dead code)
//   ۲. افزودن saveReactionAtomic با LWW merge (GET+PATCH)
//   ۳. افزودن saveReactionsBatchAtomic برای چند کاربر
//   ۴. افزودن getMessagesReactionsBatch برای بارگذاری بهینه
//   ۵. پشتیبانی کامل از reactionTs در همه توابع
//   ۶. handleFetch با timeout + retry خودکار
//   ۷. رفع markMessagesAsRead (endpoint ناموجود → batch PATCH)
//   ۸. defensive کامل: ensureArray / ensureObject / safeJsonParse
//   ۹. تضمین اینکه getChannelMessages همیشه آرایه برگرداند
//  ۱۰. حفظ تمام قابلیت‌های قبلی بدون استثنا
// ============================================================

import { normalizePhone } from '../utils/normalizePhone';
import { getToken } from '../utils/secureStorage';

// ===== تنظیمات =====
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3003';

const DEFAULT_TIMEOUT_MS = 8000;
const LONG_TIMEOUT_MS = 15000;
const MAX_RETRIES = 2;

// ============================================================
// ===== توابع دفاعی مشترک =====
// ============================================================

/**
 * ✅ همیشه یک آرایه برمی‌گرداند (حتی اگه ورودی null/object/string باشه).
 */
const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  return [];
};

/**
 * ✅ همیشه یک آبجکت ساده برمی‌گرداند.
 */
const ensureObject = (val) => {
  if (val && typeof val === 'object' && !Array.isArray(val)) return val;
  return {};
};

/**
 * ✅ JSON.parse امن با fallback.
 */
const safeJsonParse = (str, fallback) => {
  if (typeof str !== 'string' || !str) return fallback;
  try {
    const parsed = JSON.parse(str);
    return parsed !== null && parsed !== undefined ? parsed : fallback;
  } catch {
    return fallback;
  }
};

/**
 * ✅ فیلتر پیام‌های نامعتبر (null، بدون id، غیر آبجکت).
 */
const sanitizeMessagesArray = (arr) => {
  const safe = ensureArray(arr);
  return safe.filter((m) => m && typeof m === 'object' && m.id);
};

// ============================================================
// ===== توابع تولید کد و ID =====
// ============================================================
export const generateTeamCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = 'T-';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
};

// ============================================================
// ===== handleFetch با timeout + retry =====
// ============================================================

/**
 * fetch با timeout و retry خودکار برای خطاهای گذرا (5xx, network).
 * - خطاهای 4xx (bad request, not found) → بدون retry پرتاب می‌شن
 * - خطاهای 5xx یا network → تا MAX_RETRIES بار امتحان می‌شه
 */
const handleFetch = async (url, options = {}) => {
  const {
    timeout = DEFAULT_TIMEOUT_MS,
    retries = MAX_RETRIES,
    ...fetchOptions
  } = options;

  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      let token = null;
      try { token = getToken(); } catch { /* ignore */ }

      const finalHeaders = {
        ...(fetchOptions.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(url, {
        ...fetchOptions,
        headers: finalHeaders,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `خطا: ${response.status}`);
        }
        throw new Error(`خطای سرور: ${response.status}`);
      }

      if (response.status === 204) return null;

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return await response.json();
      }
      return await response.text();
    } catch (error) {
      clearTimeout(timeoutId);
      lastError = error;

      const isAbort = error.name === 'AbortError';
      const isNetwork = error instanceof TypeError;
      const is5xx = /خطای سرور: 5/.test(error.message || '');

      const shouldRetry = (isAbort || isNetwork || is5xx) && attempt < retries;
      if (!shouldRetry) break;

      const delay = 300 * Math.pow(2, attempt) + Math.random() * 200;
      await new Promise((r) => setTimeout(r, delay));
    }
  }

  console.error(`❌ Fetch error (${url}):`, lastError);
  throw lastError;
};

// ===== مرتب‌سازی گروه‌ها بر اساس جدیدترین =====
const sortTeamsByNewest = (teams) => {
  return [...ensureArray(teams)].sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });
};

// ============================================================
// ===== نرمال‌سازی ری‌اکشن‌ها (کپی سبک برای سرویس) =====
// ============================================================

const _normalizeReactions = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const norm = {};
  Object.entries(raw).forEach(([key, val]) => {
    if (Array.isArray(val)) {
      val.forEach((uid) => { if (uid) norm[uid] = key; });
    } else if (typeof val === 'string') {
      norm[key] = val;
    } else if (val && typeof val === 'object' && typeof val.emoji === 'string') {
      norm[key] = val.emoji;
    }
  });
  return norm;
};

const _normalizeReactionTs = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  Object.entries(raw).forEach(([uid, val]) => {
    if (typeof val === 'number' && Number.isFinite(val)) out[uid] = val;
  });
  return out;
};

const _mergeReactionMaps = (rA, tsA, rB, tsB) => {
  const resultR = {};
  const resultTs = {};
  const allUsers = new Set([...Object.keys(rA || {}), ...Object.keys(rB || {})]);

  allUsers.forEach((uid) => {
    const aHas = uid in (rA || {});
    const bHas = uid in (rB || {});
    const aR = aHas ? rA[uid] : undefined;
    const bR = bHas ? rB[uid] : undefined;
    const aT = tsA?.[uid] || 0;
    const bT = tsB?.[uid] || 0;

    let winner, winnerT;
    if (bHas && (!aHas || bT >= aT)) {
      winner = bR; winnerT = bT;
    } else if (aHas) {
      winner = aR; winnerT = aT;
    } else {
      return;
    }

    if (winner !== undefined && winner !== null && winner !== '') {
      resultR[uid] = winner;
    }
    resultTs[uid] = winnerT;
  });

  return { reactions: resultR, reactionTs: resultTs };
};

// ============================================================
// ===== کاربران =====
// ============================================================

export const getAllUsers = async () => {
  try {
    const [rawRootUsers, rawAppData] = await Promise.all([
      fetch(`${API_BASE}/users`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
      fetch(`${API_BASE}/appData`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ]);

    const rootUsers = ensureArray(rawRootUsers);
    const appDataObj = ensureObject(rawAppData);
    const appDataUsers = ensureArray(appDataObj.users);

    const merged = [];
    const seen = new Set();
    [...rootUsers, ...appDataUsers].forEach((u) => {
      if (u && u.id && !seen.has(u.id)) {
        seen.add(u.id);
        merged.push(u);
      }
    });

    if (merged.length > 0) {
      localStorage.setItem('users', JSON.stringify(merged));
    }
    return merged;
  } catch (error) {
    console.warn('⚠️ Error getting all users:', error);
    const stored = safeJsonParse(localStorage.getItem('users'), []);
    return ensureArray(stored);
  }
};

export const getUserById = async (id) => {
  if (!id) throw new Error('شناسه کاربر الزامی است');
  try {
    return await handleFetch(`${API_BASE}/users/${id}`);
  } catch (error) {
    console.warn('⚠️ Error getting user by id:', error);
    const stored = safeJsonParse(localStorage.getItem('users'), []);
    const users = ensureArray(stored);
    const user = users.find((u) => u && u.id === id);
    return user || null;
  }
};

export const updateUserProfile = async (userId, updates) => {
  if (!userId) throw new Error('شناسه کاربر الزامی است');
  const safeUpdates = ensureObject(updates);

  const applyLocalUpdate = () => {
    try {
      const stored = safeJsonParse(localStorage.getItem('users'), []);
      const users = ensureArray(stored);
      const index = users.findIndex((u) => u && u.id === userId);
      if (index !== -1) {
        users[index] = { ...users[index], ...safeUpdates };
        localStorage.setItem('users', JSON.stringify(users));
      }

      const currentUser = safeJsonParse(localStorage.getItem('user'), null);
      if (currentUser && currentUser.id === userId) {
        const updatedUser = { ...currentUser, ...safeUpdates };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (e) {
      console.warn('⚠️ Error updating user in localStorage:', e);
    }
  };

  try {
    const result = await handleFetch(`${API_BASE}/users/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(safeUpdates),
    });
    applyLocalUpdate();
    return result;
  } catch (error) {
    console.warn('⚠️ Error updating user profile on server:', error);
    applyLocalUpdate();
    throw error;
  }
};

export const searchUserByPhone = async (phone) => {
  if (!phone || phone.trim() === '') {
    throw new Error('شماره موبایل را وارد کنید');
  }

  const normalizedInput = normalizePhone(phone);

  try {
    const storedUsers = safeJsonParse(localStorage.getItem('users'), []);
    const users = ensureArray(storedUsers);
    const found = users.find((u) => u && normalizePhone(u.phone) === normalizedInput);
    if (found) return found;
  } catch (e) { /* ignore */ }

  try {
    const rawUsers = await handleFetch(`${API_BASE}/users`, { timeout: 5000 });
    const users = ensureArray(rawUsers);
    const found = users.find((u) => u && normalizePhone(u.phone) === normalizedInput);
    if (found) {
      const stored = safeJsonParse(localStorage.getItem('users'), []);
      const storedArr = ensureArray(stored);
      if (!storedArr.find((u) => u && u.id === found.id)) {
        storedArr.push(found);
        localStorage.setItem('users', JSON.stringify(storedArr));
      }
      return found;
    }
    return null;
  } catch (error) {
    console.warn('⚠️ سرور در دسترس نیست:', error.message);
    return null;
  }
};

export const getUserFullName = (user) => {
  if (!user) return 'کاربر';
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`.trim();
  }
  if (user.firstName) return user.firstName;
  if (user.lastName) return user.lastName;
  if (user.name) return user.name;
  if (user.fullName) return user.fullName;
  if (user.username) return user.username;
  return 'کاربر';
};

// ============================================================
// ===== گروه‌ها =====
// ============================================================

export const createTeam = async (teacherId, teamName, description = '', image = '') => {
  if (!teacherId) throw new Error('شناسه معلم الزامی است');
  if (!teamName || teamName.trim() === '') throw new Error('نام گروه الزامی است');

  try {
    const rawExisting = await handleFetch(
      `${API_BASE}/teams?teacherId=${encodeURIComponent(teacherId)}`
    );
    const existingTeams = ensureArray(rawExisting);
    if (existingTeams.length > 0) {
      const newest = sortTeamsByNewest(existingTeams)[0];
      console.warn('⚠️ Teacher already has a team. Returning existing:', newest.id);
      return newest;
    }
  } catch (e) {
    console.warn('⚠️ Could not check existing teams, proceeding with creation:', e.message);
  }

  const teamCode = generateTeamCode();
  const newTeam = {
    teacherId,
    name: teamName.trim(),
    description: description || '',
    image: image || '',
    teamCode,
    allowStudentMessaging: true,
    joinMode: 'invite_only',
    maxMembers: 50,
    representativeCanApproveRequests: false,
    createdAt: new Date().toISOString(),
  };
  return handleFetch(`${API_BASE}/teams`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newTeam),
  });
};

export const getTeamByTeacher = async (teacherId) => {
  if (!teacherId) return null;

  let teams = null;
  let serverError = null;

  try {
    const rawTeams = await handleFetch(`${API_BASE}/teams?teacherId=${encodeURIComponent(teacherId)}`);
    teams = ensureArray(rawTeams);
  } catch (error) {
    console.warn('⚠️ Server error getting team by teacher:', error);
    serverError = error;
  }

  if (teams !== null) {
    if (teams.length === 0) return null;
    if (teams.length === 1) return teams[0];

    try {
      const stored = safeJsonParse(localStorage.getItem('team'), null);
      if (stored) {
        const matched = teams.find((t) => t && t.id === stored.id);
        if (matched) {
          console.log('📦 Multiple teams found, prioritizing localStorage team:', matched.id);
          return matched;
        }
      }
    } catch (e) { /* ignore */ }

    const newest = sortTeamsByNewest(teams)[0];
    console.log('📦 Multiple teams found, returning newest:', newest.id);
    return newest;
  }

  throw serverError || new Error('خطا در دریافت گروه از سرور');
};

export const getTeamById = async (teamId) => {
  if (!teamId) return null;
  try {
    return await handleFetch(`${API_BASE}/teams/${teamId}`);
  } catch (error) {
    console.warn('⚠️ Error getting team by id:', error);
    return null;
  }
};

export const getTeamByCode = async (teamCode) => {
  if (!teamCode) return null;
  try {
    const rawTeams = await handleFetch(`${API_BASE}/teams?teamCode=${encodeURIComponent(teamCode)}`);
    const teams = ensureArray(rawTeams);
    return teams.length > 0 ? teams[0] : null;
  } catch (error) {
    console.warn('⚠️ Error getting team by code:', error);
    return null;
  }
};

export const updateTeam = async (teamId, updates) => {
  if (!teamId) throw new Error('شناسه گروه الزامی است');
  return handleFetch(`${API_BASE}/teams/${teamId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ensureObject(updates)),
  });
};

export const deleteTeam = async (teamId) => {
  if (!teamId) throw new Error('شناسه گروه الزامی است');
  await handleFetch(`${API_BASE}/teams/${teamId}`, {
    method: 'DELETE',
  });
  return true;
};

// ============================================================
// ===== اعضای گروه =====
// ============================================================

export const getTeamMembers = async (teamId) => {
  if (!teamId) return [];
  try {
    const raw = await handleFetch(`${API_BASE}/teamMembers?teamId=${teamId}`);
    return sanitizeMessagesArray(raw).map((m) => ({
      ...m,
      readBy: ensureArray(m.readBy),
    }));
  } catch (error) {
    console.warn('⚠️ Error getting team members:', error);
    return [];
  }
};

export const getAllTeamMembers = async () => {
  try {
    const [rawActive, rawPending] = await Promise.all([
      handleFetch(`${API_BASE}/teamMembers?status=active`).catch(() => []),
      handleFetch(`${API_BASE}/teamMembers?status=pending`).catch(() => []),
    ]);
    const active = ensureArray(rawActive);
    const pending = ensureArray(rawPending);
    return [...active, ...pending];
  } catch (error) {
    console.warn('⚠️ Error getting all team members:', error);
    return [];
  }
};

export const checkUserMembership = async (userId) => {
  if (!userId) return null;
  try {
    const raw = await handleFetch(`${API_BASE}/teamMembers?studentId=${userId}&status=active`);
    const members = ensureArray(raw);
    return members.length > 0 ? members[0] : null;
  } catch (error) {
    console.warn('⚠️ Error checking user membership:', error);
    return null;
  }
};

export const getMemberByStudentAndTeam = async (teamId, studentId) => {
  if (!teamId || !studentId) return null;
  try {
    const raw = await handleFetch(`${API_BASE}/teamMembers`);
    const all = ensureArray(raw);
    const found = all.find(
      (m) => m && String(m.teamId) === String(teamId) && String(m.studentId) === String(studentId)
    );
    return found || null;
  } catch (error) {
    console.warn('⚠️ Error getting member by student and team:', error);
    return null;
  }
};

export const getMemberByStudentAndCode = async (teamCode, studentId) => {
  if (!teamCode || !studentId) return null;
  const team = await getTeamByCode(teamCode);
  if (!team) return null;
  return getMemberByStudentAndTeam(team.id, studentId);
};

export const sendMemberRequest = async (teamId, studentId) => {
  if (!teamId || !studentId) throw new Error('شناسه گروه و دانش‌آموز الزامی است');

  const existing = await getMemberByStudentAndTeam(teamId, studentId);
  if (existing) {
    if (existing.status === 'active') throw new Error('این کاربر قبلاً عضو گروه است');
    if (existing.status === 'pending') throw new Error('درخواست قبلاً ارسال شده است');
    return updateMemberStatus(existing.id, 'pending');
  }

  const newMember = {
    teamId,
    studentId,
    status: 'pending',
    role: 'member',
    direction: 'teacher_invite',
    joinedAt: new Date().toISOString(),
  };
  return handleFetch(`${API_BASE}/teamMembers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newMember),
  });
};









// ============================================================
// ===== درخواست عضویت با احترام به joinMode =====
// ============================================================
export const requestJoin = async (teamId, userId) => {
  if (!teamId || !userId) {
    throw new Error('شناسه گروه و کاربر الزامی است');
  }

  // ─── ۱. خواندن گروه ───
  const team = await handleFetch(`${API_BASE}/teams/${teamId}`);
  if (!team) {
    const err = new Error('گروه یافت نشد');
    err.code = 'team_not_found';
    throw err;
  }

  const joinMode = team.joinMode || 'invite_only';
  const maxMembers = Number(team.maxMembers) || 50;

  if (joinMode === 'closed') {
    const err = new Error('گروه بسته است');
    err.code = 'group_closed';
    throw err;
  }

  // ─── ۲. بررسی عضویت قبلی ───
  const existingRaw = await handleFetch(
    `${API_BASE}/teamMembers?teamId=${encodeURIComponent(teamId)}&studentId=${encodeURIComponent(userId)}`
  );
  const existingArr = ensureArray(existingRaw);
  const activeMember = existingArr.find((m) => m && m.status === 'active');
  const pendingMember = existingArr.find((m) => m && m.status === 'pending');

  if (activeMember) {
    const err = new Error('شما قبلاً عضو این گروه هستید');
    err.code = 'already_member';
    throw err;
  }
  if (pendingMember) {
    const err = new Error('درخواست شما در انتظار تأیید است');
    err.code = 'already_pending';
    throw err;
  }

  // ─── ۳. بررسی ظرفیت ───
  const allRaw = await handleFetch(
    `${API_BASE}/teamMembers?teamId=${encodeURIComponent(teamId)}`
  );
  const activeCount = ensureArray(allRaw).filter((m) => m && m.status === 'active').length;

  if (activeCount >= maxMembers) {
    const err = new Error('ظرفیت گروه پر است');
    err.code = 'group_full';
    throw err;
  }

  // ─── ۴. ساخت member ───
  const now = Date.now();
  const memberPayload = {
    teamId,
    studentId: userId,
    role: 'member',
    direction: 'student_request',
    mode: joinMode,
    requestedAt: now,
    expiresAt: joinMode === 'invite_only' ? now + 5 * 60 * 1000 : null,
    joinedAt: new Date().toISOString(),
    status: joinMode === 'open' ? 'active' : 'pending',
  };

  const member = await handleFetch(`${API_BASE}/teamMembers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(memberPayload),
  });

  if (!member || !member.id) {
    throw new Error('خطا در ایجاد عضویت');
  }

  // ─── ۵. اگر open بود، همین‌جا تمام ───
  if (joinMode === 'open') {
    return { status: 'active', member };
  }

  // ─── ۶. حالت invite_only: ساخت پیام درخواست ───
  let userInfo = null;
  try {
    userInfo = await handleFetch(`${API_BASE}/users/${encodeURIComponent(userId)}`);
  } catch { /* ignore */ }

  let userName = `کاربر ${userId}`;
  if (userInfo) {
    if (userInfo.firstName && userInfo.lastName) {
      userName = `${userInfo.firstName} ${userInfo.lastName}`.trim();
    } else if (userInfo.name) {
      userName = userInfo.name;
    } else if (userInfo.fullName) {
      userName = userInfo.fullName;
    } else if (userInfo.username) {
      userName = userInfo.username;
    }
  }
  const userAvatar = userInfo?.avatar || '/avatars/avatar34.png';

  const messageId = generateId();
  const joinRequestMessage = {
    id: messageId,
    teamId,
    senderId: userId,
    type: 'join_request',
    content: '',
    timestamp: new Date().toISOString(),
    edited: false,
    pinned: false,
    isRead: false,
    readBy: [],
    reactions: {},
    reactionTs: {},
    joinRequest: {
      memberId: member.id,
      userId,
      userName,
      userAvatar,
      status: 'pending',
      requestedAt: now,
      expiresAt: now + 5 * 60 * 1000,
      respondedAt: null,
      respondedBy: null,
      respondedByName: null,
    },
  };

  let createdMessage = null;
  try {
    createdMessage = await handleFetch(`${API_BASE}/channelMessages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(joinRequestMessage),
    });
  } catch (e) {
    console.warn('⚠️ ساخت پیام درخواست ناموفق، rollback:', e.message);
    // rollback: member را حذف کن
    await handleFetch(`${API_BASE}/teamMembers/${member.id}`, { method: 'DELETE' }).catch(() => {});
    throw new Error('خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.');
  }

  // ─── ۷. اتصال پیام به member ───
  if (createdMessage?.id) {
    await handleFetch(`${API_BASE}/teamMembers/${member.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requestMessageId: createdMessage.id }),
    }).catch(() => { /* غیرحیاتی */ });
  }

  return {
    status: 'pending',
    member,
    messageId: createdMessage?.id || messageId,
    message: createdMessage,
  };
};









// ============================================================
// ===== پاسخ به درخواست عضویت (accept/reject) =====
// ============================================================
export const respondToJoinRequest = async (
  messageId,
  action,
  responderId,
  responderName
) => {
  if (!messageId || !action || !responderId) {
    throw new Error('پارامترهای ناقص');
  }
  if (!['accept', 'reject'].includes(action)) {
    throw new Error('عملیات نامعتبر');
  }

  // ─── ۱. خواندن پیام ───
  const message = await handleFetch(`${API_BASE}/channelMessages/${messageId}`);
  if (!message) {
    const err = new Error('پیام درخواست یافت نشد');
    err.code = 'message_not_found';
    throw err;
  }
  if (message.type !== 'join_request' || !message.joinRequest) {
    const err = new Error('این پیام درخواست عضویت نیست');
    err.code = 'invalid_message_type';
    throw err;
  }

  const jr = message.joinRequest;
  if (jr.status !== 'pending') {
    const err = new Error('این درخواست قبلاً پاسخ داده شده است');
    err.code = 'already_responded';
    throw err;
  }

  // ─── ۲. بررسی انقضا ───
  const now = Date.now();
  if (jr.expiresAt && now >= jr.expiresAt) {
    // auto-expire
    await handleFetch(`${API_BASE}/channelMessages/${messageId}`, {
      method: 'DELETE',
    }).catch(() => {});
    await handleFetch(`${API_BASE}/teamMembers/${jr.memberId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'expired' }),
    }).catch(() => {});
    const err = new Error('این درخواست منقضی شده است');
    err.code = 'expired';
    throw err;
  }

  // ─── ۳. اگر accept بود، چک ظرفیت ───
  if (action === 'accept') {
    const team = await handleFetch(`${API_BASE}/teams/${message.teamId}`);
    const maxMembers = Number(team?.maxMembers) || 50;
    const allRaw = await handleFetch(
      `${API_BASE}/teamMembers?teamId=${encodeURIComponent(message.teamId)}`
    );
    const activeCount = ensureArray(allRaw).filter((m) => m && m.status === 'active').length;
    if (activeCount >= maxMembers) {
      const err = new Error('ظرفیت گروه پر شده است. ابتدا یک عضو را حذف کنید.');
      err.code = 'capacity_full';
      throw err;
    }
  }

  // ─── ۴. آپدیت member ───
  const memberStatus = action === 'accept' ? 'active' : 'rejected';
  await handleFetch(`${API_BASE}/teamMembers/${jr.memberId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      status: memberStatus,
      respondedAt: now,
      respondedBy: responderId,
      ...(action === 'accept' ? { joinedAt: new Date().toISOString() } : {}),
    }),
  });

  // ─── ۵. آپدیت پیام ───
  const newJoinRequest = {
    ...jr,
    status: action === 'accept' ? 'accepted' : 'rejected',
    respondedAt: now,
    respondedBy: responderId,
    respondedByName: responderName || 'مدیر',
  };

  await handleFetch(`${API_BASE}/channelMessages/${messageId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ joinRequest: newJoinRequest }),
  });

  // ─── ۶. اگر accept بود، پیام سیستم ───
  if (action === 'accept') {
    const systemMsg = {
      id: generateId(),
      teamId: message.teamId,
      senderId: responderId,
      type: 'system',
      systemSubtype: 'join_accepted',
      content: `درخواست عضویت ${jr.userName} توسط ${responderName || 'مدیر'} پذیرفته شد`,
      meta: {
        userId: jr.userId,
        responderId,
        memberId: jr.memberId,
      },
      timestamp: new Date().toISOString(),
      edited: false,
      pinned: false,
      isRead: false,
      readBy: [],
      reactions: {},
      reactionTs: {},
    };

    await handleFetch(`${API_BASE}/channelMessages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(systemMsg),
    }).catch((e) => console.warn('⚠️ پیام سیستم ناموفق:', e.message));
  }

  return { success: true, action, memberStatus, messageId };
};







// ============================================================
// ===== پاک‌سازی درخواست‌های منقضی‌شده =====
// ============================================================
export const expireJoinRequests = async (teamId) => {
  if (!teamId) return { cleaned: 0 };

  try {
    const messagesRaw = await handleFetch(
      `${API_BASE}/channelMessages?teamId=${encodeURIComponent(teamId)}&type=join_request`
    );
    const messages = ensureArray(messagesRaw);
    const now = Date.now();
    let cleaned = 0;

    for (const msg of messages) {
      if (!msg?.joinRequest) continue;
      if (msg.joinRequest.status !== 'pending') continue;
      if (!msg.joinRequest.expiresAt) continue;
      if (msg.joinRequest.expiresAt > now) continue;

      // منقضی شده → پاک کن
      try {
        await handleFetch(`${API_BASE}/channelMessages/${msg.id}`, {
          method: 'DELETE',
        });
        await handleFetch(`${API_BASE}/teamMembers/${msg.joinRequest.memberId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'expired' }),
        }).catch(() => {});
        cleaned++;
      } catch (e) {
        console.warn('⚠️ expire یک درخواست ناموفق:', msg.id, e.message);
      }
    }

    return { cleaned };
  } catch (e) {
    console.warn('expireJoinRequests error:', e);
    return { cleaned: 0 };
  }
};






export const updateMemberStatus = async (memberId, status) => {
  if (!memberId) throw new Error('شناسه عضو الزامی است');
  if (!['pending', 'active', 'rejected'].includes(status)) {
    throw new Error('وضعیت نامعتبر است');
  }
  return handleFetch(`${API_BASE}/teamMembers/${memberId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
};

export const updateMemberRole = async (memberId, role) => {
  if (!memberId) throw new Error('شناسه عضو الزامی است');
  if (!['member', 'moderator', 'admin'].includes(role)) {
    throw new Error('نقش نامعتبر است');
  }
  return handleFetch(`${API_BASE}/teamMembers/${memberId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
};

export const deleteMember = async (memberId) => {
  if (!memberId) throw new Error('شناسه عضو الزامی است');
  await handleFetch(`${API_BASE}/teamMembers/${memberId}`, {
    method: 'DELETE',
  });
  return true;
};

export const updateMemberTeamCode = async (memberId, teamCode) => {
  if (!memberId) throw new Error('شناسه عضو الزامی است');
  return handleFetch(`${API_BASE}/teamMembers/${memberId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ teamCode }),
  });
};

// ============================================================
// ===== پیام‌های کانال (گروهی) =====
// ============================================================

export const sendChannelMessage = async (
  teamId,
  senderId,
  content,
  type = 'text',
  replyTo = null,
  pollData = null
) => {
  if (!teamId || !senderId) throw new Error('شناسه گروه و فرستنده الزامی است');
  if (!content && type === 'text' && !pollData) {
    throw new Error('متن پیام یا داده‌های نظرسنجی الزامی است');
  }

  const newMsg = {
    teamId,
    senderId,
    type,
    content: content || '',
    replyTo,
    timestamp: new Date().toISOString(),
    edited: false,
    pinned: false,
    isRead: false,
    readBy: [],
    pollData: pollData || null,
    reactions: {},
    reactionTs: {},
  };
  return handleFetch(`${API_BASE}/channelMessages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newMsg),
  });
};

/**
 * ✅ getChannelMessages - همیشه { data: Array, total: number } برمی‌گرداند.
 * حتی اگه سرور `{}` یا `null` یا رشته برگردونه، data آرایه‌ی خالیه.
 */
export const getChannelMessages = async (teamId, page = 1, limit = 50, filter = {}) => {
  const EMPTY = { data: [], total: 0 };
  if (!teamId) return EMPTY;

  try {
    // ✅ _order=desc: جدیدترین‌ها اول
    let url = `${API_BASE}/channelMessages?teamId=${encodeURIComponent(teamId)}&_sort=timestamp&_order=desc&_page=${page}&_limit=${limit}`;
    const safeFilter = ensureObject(filter);
    if (safeFilter.type) url += `&type=${encodeURIComponent(safeFilter.type)}`;
    if (safeFilter.senderId) url += `&senderId=${encodeURIComponent(safeFilter.senderId)}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), LONG_TIMEOUT_MS);

    try {
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error('خطا در دریافت پیام‌های کانال');

      const totalRaw = res.headers.get('X-Total-Count');
      const totalParsed = parseInt(totalRaw || '0', 10);
      const total = Number.isFinite(totalParsed) && totalParsed >= 0 ? totalParsed : 0;

      let rawData;
      try { rawData = await res.json(); } catch { rawData = []; }

      const data = sanitizeMessagesArray(rawData)
        .map((m) => ({
          ...m,
          readBy: ensureArray(m.readBy),
          reactions: _normalizeReactions(m.reactions),
          reactionTs: _normalizeReactionTs(m.reactionTs),
        }))
        .reverse(); // ✅ برگردون به ترتیب زمانی صعودی (قدیمی → جدید) برای نمایش

      return { data, total: total || data.length };
    } catch (e) {
      clearTimeout(timeoutId);
      throw e;
    }
  } catch (error) {
    console.warn('⚠️ Error getting channel messages:', error);
    return EMPTY;
  }
};

export const deleteChannelMessagesBulk = async (messageIds) => {
  const ids = ensureArray(messageIds).filter((id) => id);
  if (ids.length === 0) return true;

  const BATCH_SIZE = 50;
  const batches = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    batches.push(ids.slice(i, i + BATCH_SIZE));
  }

  let totalFailed = 0;
  for (const batch of batches) {
    const results = await Promise.allSettled(
      batch.map((id) => fetch(`${API_BASE}/channelMessages/${id}`, { method: 'DELETE' }))
    );
    const failed = results.filter((r) => r.status === 'rejected' || !r.value?.ok);
    totalFailed += failed.length;
  }

  if (totalFailed > 0) {
    console.warn(`⚠️ ${totalFailed} messages failed to delete`);
    throw new Error(`خطا در حذف ${totalFailed} پیام`);
  }
  return true;
};

export const updateChannelMessage = async (messageId, updates) => {
  if (!messageId) throw new Error('شناسه پیام الزامی است');
  return handleFetch(`${API_BASE}/channelMessages/${messageId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ensureObject(updates)),
  });
};

export const deleteChannelMessage = async (messageId) => {
  if (!messageId) throw new Error('شناسه پیام الزامی است');
  await handleFetch(`${API_BASE}/channelMessages/${messageId}`, {
    method: 'DELETE',
  });
  return true;
};

export const pinChannelMessage = async (messageId, pinned = true) => {
  if (!messageId) throw new Error('شناسه پیام الزامی است');
  return handleFetch(`${API_BASE}/channelMessages/${messageId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pinned }),
  });
};

/**
 * علامت‌گذاری پیام‌ها به‌عنوان خوانده‌شده.
 * با batch PATCH + defensive.
 */
export const markMessagesAsRead = async (teamId, userId, messageIds) => {
  const ids = ensureArray(messageIds).filter((id) => id);
  if (!teamId || !userId || ids.length === 0) {
    return { updated: 0, failed: 0 };
  }

  const BATCH_SIZE = 25;
  const batches = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    batches.push(ids.slice(i, i + BATCH_SIZE));
  }

  let totalUpdated = 0;
  let totalFailed = 0;

  for (const batch of batches) {
    const results = await Promise.allSettled(
      batch.map(async (id) => {
        const msg = await handleFetch(`${API_BASE}/channelMessages/${id}`, { timeout: 5000 });
        if (!msg) return null;

        const currentReadBy = ensureArray(msg.readBy);
        if (currentReadBy.includes(userId)) return null;

        return await handleFetch(`${API_BASE}/channelMessages/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ readBy: [...currentReadBy, userId] }),
          timeout: 5000,
        });
      })
    );

    results.forEach((r) => {
      if (r.status === 'fulfilled' && r.value) totalUpdated++;
      else if (r.status === 'rejected') totalFailed++;
    });
  }

  return { updated: totalUpdated, failed: totalFailed };
};

// ============================================================
// ===== نظرسنجی (Poll) =====
// ============================================================

export const createPoll = async (teamId, senderId, question, options, config = {}) => {
  if (!teamId || !senderId) throw new Error('شناسه گروه و فرستنده الزامی است');
  if (!question || question.trim() === '') throw new Error('سؤال نظرسنجی الزامی است');
  const safeOptions = ensureArray(options);
  if (safeOptions.length < 2) throw new Error('حداقل ۲ گزینه لازم است');
  if (safeOptions.length > 12) throw new Error('حداکثر ۱۲ گزینه مجاز است');

  const safeConfig = ensureObject(config);

  // ✅ محاسبه‌ی زمان انقضا
let expiresAt = null;
if (safeConfig.duration && safeConfig.duration.value > 0) {
  const unitMs =
    safeConfig.duration.unit === 'second' ? 1000 :
    safeConfig.duration.unit === 'minute' ? 60 * 1000 :
    safeConfig.duration.unit === 'hour'   ? 60 * 60 * 1000 :
    safeConfig.duration.unit === 'day'    ? 24 * 60 * 60 * 1000 :
    60 * 1000;
  expiresAt = Date.now() + safeConfig.duration.value * unitMs;
}


  const pollData = {
    question: question.trim(),
    options: safeOptions.map((opt) => ({ text: String(opt || '').trim(), votes: 0, voterNames: [] })),
    type: safeConfig.type || 'single',
    anonymous: !!safeConfig.anonymous,
    isQuiz: !!safeConfig.isQuiz,
    correctOption: safeConfig.isQuiz ? safeConfig.correctOption : undefined,
    duration: safeConfig.duration || null,
    expiresAt: expiresAt,
    hideResults: !!safeConfig.hideResults,
    shuffleOptions: !!safeConfig.shuffleOptions,
    allowChangeVote: safeConfig.allowChangeVote !== undefined ? safeConfig.allowChangeVote : true,
    allowSuggest: !!safeConfig.allowSuggest,
    description: safeConfig.description || '',
    isClosed: false,
    totalVotes: 0,
    votedBy: [],
    userVotes: {},
    createdAt: new Date().toISOString(),
    closedAt: null,
  };

  return sendChannelMessage(teamId, senderId, '', 'poll', null, pollData);
};

export const votePoll = async (pollId, userId, optionIndex) => {
  if (!pollId || !userId) throw new Error('شناسه نظرسنجی و کاربر الزامی است');
  if (optionIndex === undefined || optionIndex === null) throw new Error('شماره گزینه الزامی است');

  const pollMsg = await getChannelMessage(pollId);
  if (!pollMsg) throw new Error('نظرسنجی یافت نشد');
  if (pollMsg.type !== 'poll') throw new Error('این پیام نظرسنجی نیست');
  if (!pollMsg.pollData) throw new Error('داده‌های نظرسنجی موجود نیست');
  if (pollMsg.pollData.isClosed) throw new Error('این نظرسنجی بسته شده است');
  if (pollMsg.pollData.expiresAt && Date.now() >= pollMsg.pollData.expiresAt) {
  throw new Error('⏰ زمان این نظرسنجی به پایان رسیده است');
}

  // ✅ Deep copy برای جلوگیری از mutation ناخواسته
  const pollData = JSON.parse(JSON.stringify(pollMsg.pollData));
  pollData.options = ensureArray(pollData.options);
  pollData.userVotes = ensureObject(pollData.userVotes);
  pollData.votedBy = ensureArray(pollData.votedBy);

  const opt = pollData.options[optionIndex];
  if (!opt) throw new Error('گزینه نامعتبر است');

  const user = await getUserById(userId);
  const userName = user ? getUserFullName(user) : `کاربر ${userId}`;

  const isMultiple = pollData.type === 'multiple';
  const currentVote = pollData.userVotes[userId];
  const hasVotedBefore = currentVote !== undefined && currentVote !== null;

  if (hasVotedBefore && !pollData.allowChangeVote) {
    throw new Error('شما قبلاً رأی داده‌اید و نمی‌توانید رأی خود را تغییر دهید');
  }

  const removeVoteFromOption = (idx) => {
    const o = pollData.options[idx];
    if (!o) return;
    o.votes = Math.max(0, (o.votes || 0) - 1);
    pollData.totalVotes = Math.max(0, (pollData.totalVotes || 0) - 1);
    if (!pollData.anonymous && Array.isArray(o.voterNames)) {
      o.voterNames = o.voterNames.filter((n) => n !== userName);
    }
  };

  const addVoteToOption = (idx) => {
    const o = pollData.options[idx];
    if (!o) return;
    o.votes = (o.votes || 0) + 1;
    pollData.totalVotes = (pollData.totalVotes || 0) + 1;
    if (!pollData.anonymous) {
      o.voterNames = ensureArray(o.voterNames);
      if (!o.voterNames.includes(userName)) o.voterNames.push(userName);
    }
  };

  if (isMultiple) {
    const userVoteList = Array.isArray(currentVote) ? currentVote : [];
    const wasSelected = userVoteList.includes(optionIndex);

    if (wasSelected) {
      removeVoteFromOption(optionIndex);
      pollData.userVotes[userId] = userVoteList.filter((i) => i !== optionIndex);
    } else {
      addVoteToOption(optionIndex);
      pollData.userVotes[userId] = [...userVoteList, optionIndex];
    }
  } else {
    if (hasVotedBefore && currentVote !== optionIndex) {
      removeVoteFromOption(currentVote);
    }
    if (!hasVotedBefore || currentVote !== optionIndex) {
      addVoteToOption(optionIndex);
      pollData.userVotes[userId] = optionIndex;
    }
  }

  if (!pollData.votedBy.some((id) => String(id) === String(userId))) {
    pollData.votedBy.push(userId);
  }

  return updateChannelMessage(pollId, { pollData });
};

export const closePoll = async (pollId) => {
  if (!pollId) throw new Error('شناسه نظرسنجی الزامی است');
  const pollMsg = await getChannelMessage(pollId);
  if (!pollMsg) throw new Error('نظرسنجی یافت نشد');
  if (pollMsg.type !== 'poll') throw new Error('این پیام نظرسنجی نیست');
  if (!pollMsg.pollData) throw new Error('داده‌های نظرسنجی موجود نیست');
  if (pollMsg.pollData.isClosed) throw new Error('این نظرسنجی قبلاً بسته شده است');

  const pollData = { ...pollMsg.pollData, isClosed: true, closedAt: new Date().toISOString() };
  return updateChannelMessage(pollId, { pollData });
};

export const deletePoll = async (pollId) => {
  return deleteChannelMessage(pollId);
};

export const pinPoll = async (pollId) => {
  return pinChannelMessage(pollId, true);
};

export const suggestPollOption = async (pollId, userId, newOptionText) => {
  if (!pollId || !userId) throw new Error('شناسه نظرسنجی و کاربر الزامی است');
  if (!newOptionText || newOptionText.trim() === '') throw new Error('متن گزینه جدید الزامی است');

  const pollMsg = await getChannelMessage(pollId);
  if (!pollMsg) throw new Error('نظرسنجی یافت نشد');
  if (pollMsg.type !== 'poll') throw new Error('این پیام نظرسنجی نیست');
  if (!pollMsg.pollData) throw new Error('داده‌های نظرسنجی موجود نیست');
  if (pollMsg.pollData.isClosed) throw new Error('این نظرسنجی بسته شده است');
  if (!pollMsg.pollData.allowSuggest) throw new Error('پیشنهاد گزینه جدید در این نظرسنجی مجاز نیست');

  const existing = ensureArray(pollMsg.pollData.options);
  if (existing.length >= 12) throw new Error('حداکثر ۱۲ گزینه مجاز است');

  const pollData = { ...pollMsg.pollData };
  pollData.options = [
    ...existing,
    { text: newOptionText.trim(), votes: 0, voterNames: [] },
  ];

  return updateChannelMessage(pollId, { pollData });
};

export const getPollResults = async (pollId) => {
  if (!pollId) throw new Error('شناسه نظرسنجی الزامی است');
  const pollMsg = await getChannelMessage(pollId);
  if (!pollMsg) throw new Error('نظرسنجی یافت نشد');
  if (pollMsg.type !== 'poll') throw new Error('این پیام نظرسنجی نیست');
  if (!pollMsg.pollData) throw new Error('داده‌های نظرسنجی موجود نیست');
  return pollMsg.pollData;
};

export const getChannelMessage = async (messageId) => {
  if (!messageId) return null;
  try {
    return await handleFetch(`${API_BASE}/channelMessages/${messageId}`, { timeout: 5000 });
  } catch {
    const stored = safeJsonParse(localStorage.getItem('channelMessages'), []);
    const arr = ensureArray(stored);
    return arr.find((m) => m && m.id === messageId) || null;
  }
};

// ============================================================
// ===== توابع اتمیک ری‌اکشن =====
// ============================================================

/**
 * ذخیره اتمیک ری‌اکشن یک کاربر روی یک پیام با LWW merge.
 */
export const saveReactionAtomic = async (messageId, localReactions, localReactionTs) => {
  if (!messageId) throw new Error('شناسه پیام الزامی است');

  const localR = _normalizeReactions(localReactions);
  const localTs = _normalizeReactionTs(localReactionTs);

  let serverMsg;
  try {
    serverMsg = await handleFetch(`${API_BASE}/channelMessages/${messageId}`, { timeout: 5000 });
  } catch (e) {
    console.warn('⚠️ saveReactionAtomic: GET failed, aborting to preserve data:', e.message);
    return null;
  }

  if (!serverMsg) return null;

  const serverR = _normalizeReactions(serverMsg.reactions);
  const serverTs = _normalizeReactionTs(serverMsg.reactionTs);
  const merged = _mergeReactionMaps(localR, localTs, serverR, serverTs);

  try {
    const saved = await handleFetch(`${API_BASE}/channelMessages/${messageId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        reactions: merged.reactions,
        reactionTs: merged.reactionTs,
      }),
      timeout: 5000,
    });
    return saved;
  } catch (e) {
    console.warn('⚠️ saveReactionAtomic: PATCH failed:', e.message);
    throw e;
  }
};

/**
 * ذخیره اتمیک چند پیام به‌صورت موازی (با محدودیت همزمانی).
 */
export const saveReactionsBatchAtomic = async (items, concurrency = 3) => {
  const safeItems = ensureArray(items);
  if (safeItems.length === 0) return [];

  const results = new Array(safeItems.length).fill(null);
  let index = 0;

  const worker = async () => {
    while (index < safeItems.length) {
      const myIdx = index++;
      const item = safeItems[myIdx];
      try {
        results[myIdx] = await saveReactionAtomic(
          item.messageId,
          item.reactions,
          item.reactionTs
        );
      } catch (e) {
        results[myIdx] = { error: e.message };
      }
    }
  };

  const workers = [];
  for (let i = 0; i < Math.min(concurrency, safeItems.length); i++) {
    workers.push(worker());
  }
  await Promise.all(workers);

  return results;
};

/**
 * بارگذاری گروهی ری‌اکشن‌های چند پیام.
 */
export const getMessagesReactionsBatch = async (messageIds) => {
  const ids = ensureArray(messageIds).filter((id) => id);
  if (ids.length === 0) return {};

  const BATCH_SIZE = 25;
  const result = {};

  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const batch = ids.slice(i, i + BATCH_SIZE);
    const query = batch.map((id) => `id=${encodeURIComponent(id)}`).join('&');

    try {
      const rawMessages = await handleFetch(
        `${API_BASE}/channelMessages?${query}`,
        { timeout: LONG_TIMEOUT_MS }
      );

      const messages = ensureArray(rawMessages);
      messages.forEach((msg) => {
        if (msg && msg.id) {
          result[msg.id] = {
            reactions: _normalizeReactions(msg.reactions),
            reactionTs: _normalizeReactionTs(msg.reactionTs),
          };
        }
      });
    } catch (e) {
      console.warn('⚠️ getMessagesReactionsBatch: batch failed:', e.message);
      try {
        const stored = safeJsonParse(localStorage.getItem('channelMessages'), []);
        const rStore = safeJsonParse(localStorage.getItem('messageReactions'), {});
        const tStore = safeJsonParse(localStorage.getItem('messageReactionTs'), {});
        const storedArr = ensureArray(stored);
        const rObj = ensureObject(rStore);
        const tObj = ensureObject(tStore);

        batch.forEach((id) => {
          const msg = storedArr.find((m) => m && m.id === id);
          if (msg) {
            result[id] = {
              reactions: _normalizeReactions(rObj[id] || msg.reactions || {}),
              reactionTs: _normalizeReactionTs(tObj[id] || msg.reactionTs || {}),
            };
          }
        });
      } catch { /* ignore */ }
    }
  }

  return result;
};

// ============================================================
// ===== فعالیت‌ها (Activity Log) =====
// ============================================================

export const addActivityLog = async (teamId, userId, action, details) => {
  if (!teamId || !userId) return null;
  try {
    const entry = {
      teamId,
      userId,
      action: action || 'unknown',
      details: details || '',
      timestamp: new Date().toISOString(),
    };
    return handleFetch(`${API_BASE}/activityLogs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entry),
      timeout: 5000,
    });
  } catch (error) {
    console.warn('⚠️ Error adding activity log:', error);
    return null;
  }
};

export const getActivityLogs = async (teamId, limit = 50) => {
  if (!teamId) return [];
  try {
    const raw = await handleFetch(
      `${API_BASE}/activityLogs?teamId=${teamId}&_sort=timestamp&_order=desc&_limit=${limit}`,
      { timeout: 5000 }
    );
    return ensureArray(raw);
  } catch (error) {
    console.warn('⚠️ Error getting activity logs:', error);
    return [];
  }
};

// ============================================================
// ===== WebSocket =====
// ============================================================

export const createWebSocketConnection = (userId, onMessage, onOpen, onClose, onError) => {
  if (!userId) {
    throw new Error('شناسه کاربر برای اتصال WebSocket الزامی است');
  }

  let ws = null;
  let heartbeatInterval = null;

  try {
    ws = new WebSocket(WS_URL);
  } catch (error) {
    console.warn('⚠️ WebSocket not available, using localStorage only:', error);
    throw new Error('اتصال به WebSocket ممکن نیست');
  }

  const startHeartbeat = () => {
    if (heartbeatInterval) clearInterval(heartbeatInterval);
    heartbeatInterval = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
        } catch (e) { /* ignore */ }
      }
    }, 30000);
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  };

  ws.onopen = () => {
    try {
      ws.send(JSON.stringify({ type: 'auth', userId }));
    } catch (e) {
      console.warn('⚠️ WebSocket auth failed:', e);
    }
    startHeartbeat();
    if (onOpen) onOpen();
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data && data.type === 'pong') return;
      if (onMessage) onMessage(data);
    } catch (e) {
      console.warn('⚠️ Invalid WebSocket message:', e);
    }
  };

  ws.onclose = () => {
    stopHeartbeat();
    if (onClose) onClose();
  };

  ws.onerror = (error) => {
    console.warn('⚠️ WebSocket error:', error);
    if (onError) onError(error);
  };

  ws.__cleanup = () => {
    stopHeartbeat();
  };

  return ws;
};

// ============================================================
// ===== Export توابع داخلی برای تست =====
// ============================================================
export const __internal = {
  handleFetch,
  ensureArray,
  ensureObject,
  safeJsonParse,
  sanitizeMessagesArray,
  normalizeReactions: _normalizeReactions,
  normalizeReactionTs: _normalizeReactionTs,
  mergeReactionMaps: _mergeReactionMaps,
};